/**
 * a2ui-subset schema shared with the backend `render_ui` agent tool.
 *
 * The backend stores a component tree on a Task payload; the frontend
 * (`UiRenderer`) interprets it into design-system components.
 *
 * Mirrors brian's `modules/task/ui_schema.py`, the two must move together.
 */

/** A node's semantic status, never its appearance: `toneStyle` in `UiPrimitives`
    decides the look. Absent tone reads as `neutral`. */
export type UiTone = "neutral" | "info" | "success" | "warning" | "urgent";

/** The closed icon catalogue, owned by the backend and mirrored here (`UI_ICONS`
    in `uiIcons.ts`); adding a name without an icon is a compile error. */
export type IconName =
  | "triangle-exclamation"
  | "circle-exclamation"
  | "circle-info"
  | "circle-check"
  | "circle-xmark"
  | "clipboard"
  | "clipboard-list"
  | "notes-medical"
  | "file-lines"
  | "stethoscope"
  | "heart-pulse"
  | "syringe"
  | "pills"
  | "vial"
  | "microscope"
  | "weight-scale"
  | "temperature-half"
  | "droplet"
  | "paw"
  | "calendar"
  | "calendar-check"
  | "clock"
  | "user"
  | "user-doctor"
  | "phone"
  | "envelope"
  | "bell"
  | "chart-line"
  | "list-check"
  | "comment";

/** One `fields` row: a measurement, identifier or setting and its reading. */
export interface UiFieldItem {
  label: string;
  value: string;
  icon?: IconName;
  /** Flags the *value*, e.g. an out-of-range vital or an overdue date. */
  tone?: UiTone;
  /** Secondary detail under the value, e.g. a reference range or a timestamp. */
  note?: string;
}

/** One `list` row: a line of text with an optional leading icon and trailing tag. */
export interface UiListItem {
  text: string;
  icon?: IconName;
  /** Trailing tag, e.g. an alert's category. Coloured by the row's `tone`. */
  badge?: string;
  tone?: UiTone;
}

export type UiNode =
  | {
      type: "stack" | "row";
      children: UiNode[];
      /** `row` only: push children to the edges (label left, controls right). */
      justify?: "between";
      /** `row` only: let children flow onto more lines instead of squeezing. */
      wrap?: boolean;
      /** `row` only: cross-axis alignment. Defaults to `center`. */
      align?: "start" | "center" | "end" | "baseline";
    }
  | {
      /** A column grid that collapses to one column in a narrow pane (the
          notifications dock vs. the wide subject reader). */
      type: "grid";
      columns: 2 | 3;
      children: UiNode[];
    }
  | {
      /** A titled panel inside a card (e.g. "Vitals", "Today's plan"). Visually
          distinct from its host card; never inherits its severity tint. */
      type: "section";
      children: UiNode[];
      title?: string;
      icon?: IconName;
      tone?: UiTone;
    }
  | {
      /** A toned emphasis block meant to be read first. `tone` is required: an
          untoned emphasis block is a `section`. */
      type: "callout";
      tone: UiTone;
      children: UiNode[];
      title?: string;
      icon?: IconName;
    }
  | {
      /** Display-only label/value rows (vitals, weights, contact details).
          Labels left, values right. */
      type: "fields";
      items: UiFieldItem[];
    }
  | {
      /** Display-only lines of text with optional icons and trailing tags
          (alerts, reminders, a checklist read-out). */
      type: "list";
      items: UiListItem[];
      /** Separate rows with hairlines, for lists read one row at a time. */
      divided?: boolean;
    }
  | { type: "divider" }
  | {
      type: "text";
      text: string;
      /** Typographic role, defaults to `body`. `heading` keeps its brand dot,
          indenting the text to line up with the option chips below. */
      variant?: "title" | "heading" | "subheading" | "body" | "caption";
      tone?: UiTone;
    }
  | {
      /** Display-only status pill — e.g. a summary alert's "Safety" tag. Inert:
          no binding, no action. Can appear anywhere `text` can (the backend
          composes an alert as a `row` of `[badge, text]`). */
      type: "badge";
      text: string;
      variant?: "red" | "orange" | "yellow" | "blue" | "gray" | "green";
      icon?: IconName;
    }
  | {
      type: "input";
      bind: string;
      placeholder?: string;
      /** Form-submitting buttons stay disabled until this field holds a value. */
      required?: boolean;
    }
  | {
      type: "select";
      bind: string;
      label?: string;
      options: { label: string; value: string }[];
      /** Form-submitting buttons stay disabled until this field holds a value. */
      required?: boolean;
    }
  | { type: "checkbox"; bind: string; text?: string }
  | {
      type: "button";
      text: string;
      action: string;
      variant?: "primary" | "secondary" | "danger";
      /** Supporting detail under `text`; also renders the button as a choice row. */
      description?: string;
      /** Merged into the submitted `data` when pressed — how a choice button
          carries which choice it is (e.g. `{ selected_option_id: "jane" }`). */
      payload?: Record<string, unknown>;
      /** Pressing this button completes the task (the backend decides that at
          submit time). The renderer reads it for one thing: Enter in an
          `input` presses the tree's single completing no-`payload` button. */
      completes?: boolean;
    }
  | { type: "card"; children: UiNode[]; dismissible?: boolean }
  | {
      /** Display-only tabular data. `columns` is the header row; each `rows`
          entry is a list of cell strings aligned to `columns`. No binding. */
      type: "table";
      columns: string[];
      rows: string[][];
    };

export interface UiPayload {
  render_id: string;
  root: UiNode;
  data?: Record<string, unknown>;
  /**
   * Set by the backend (`PATCH /tasks/{uuid}`) once staff submit. Its presence
   * means the UI was already answered, so `outstandingTasks` filters it out.
   */
  submitted_action?: string;
  /**
   * Stamped by the backend (`TaskOut`): true when the tree has no completing
   * button, so the card's only resolution is the dismiss ✕ — an alert. The
   * renderer keeps such a card clearable on every surface, including the feed,
   * rather than re-deriving "completing" itself (BE-1877). Absent on payloads
   * served without it (e.g. SavedViews); treat absent as `false`.
   */
  dismiss_only?: boolean;
}

export const isUiPayload = (value: unknown): value is UiPayload => {
  if (typeof value !== "object" || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate.render_id === "string" &&
    typeof candidate.root === "object" &&
    candidate.root !== null
  );
};
